import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteMode } from "@/context/SiteModeContext";
import { SimplePayoutsPage } from "@/components/backoffice/SimplePages";
import { ArrowRight, Banknote, CreditCard, Info, Loader2 } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { PageHead, Section, TONES } from "@/components/account/Shell";
import api from "@/lib/api";
import StripeRegister from "@/components/mall/StripeRegister";

const TONE = TONES.settings;

function Tile({ icon: Icon, label, value, note, testid }) {
    return (
        <div className="bg-white p-3" data-testid={testid}>
            <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">
                <Icon size={11} /> {label}
            </p>
            <p className="mt-1.5 text-lg font-semibold tracking-tight text-zinc-900">{value}</p>
            {note && <p className="mt-0.5 text-xs text-zinc-700">{note}</p>}
        </div>
    );
}

/** Payouts on their own screen: connect Stripe, see 13thav's cut, know what's free. */
function ClassicPayouts() {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        api.get("/store/stripe").then(({ data }) => setInfo(data)).catch(() => setInfo({ error: true }));
    }, []);

    return (
        <BackOffice>
            <div data-testid="payouts-page">
                <PageHead kicker="Shop settings" tone={TONE} title="Payouts" testid="payouts-head"
                    sub="Your register runs on your own Stripe account. Card money for your jobs lands with you — 13thav only keeps its cut, and it never touches classified ads or classes." />

                {info === null ? (
                    <div className="flex min-h-[10vh] items-center justify-center">
                        <Loader2 className="animate-spin text-zinc-400" />
                    </div>
                ) : info.error ? (
                    <p className="mt-4 rounded-lg border border-black/10 bg-white p-3 text-sm text-zinc-800"
                        data-testid="payouts-no-store">
                        Rent a space first and your register turns on.
                        <Link to="/account/sell" className="ml-2 inline-flex items-center gap-1 font-semibold text-zinc-900 hover:underline">
                            See the spaces <ArrowRight size={12} />
                        </Link>
                    </p>
                ) : (
                    <div className="mt-4 grid gap-px overflow-hidden rounded-lg border border-black/10 bg-black/10 sm:grid-cols-3">
                        <Tile icon={Banknote} label="13thav's cut" value={`${info.fee_pct}%`}
                            note="of card sales on products" testid="payouts-fee" />
                        <Tile icon={Info} label="No fee at all" value={(info.free_forever || []).join(" · ")}
                            note="these never run through Stripe" testid="payouts-free" />
                        <Tile icon={CreditCard} label="Your register"
                            value={info.status?.charges_enabled ? "Taking cards" : "Cards off"}
                            note={info.status?.payouts_enabled ? "Payouts enabled" : "Connect below to switch payouts on"}
                            testid="payouts-register" />
                    </div>
                )}

                <div className="mt-3">
                    <Section title="Your Stripe account" tone={TONE} testid="payouts-connect"
                        note="13thav never holds your money">
                        <StripeRegister />
                    </Section>
                </div>
            </div>
        </BackOffice>
    );
}

export default function Payouts() {
    const { isSimple } = useSiteMode();
    return isSimple ? <SimplePayoutsPage /> : <ClassicPayouts />;
}
